import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ArrowLeft, BookOpen, Plus, Trash2, Save, Upload, Download, FileText, Loader2, RotateCcw, BookmarkPlus, X, CheckCircle2, History, Sparkles, FileUp, Search, Copy, AlertTriangle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import type { LibraryPosition, InsertLibraryPosition, Project, PriceSample } from "@shared/schema";
import type { DuplicateGroup } from "@shared/library-pricing";
import { STAMMDATEN, type LVPosition } from "@/components/project/leistungsverzeichnis-kalkulator";
import { useIsMobile } from "@/hooks/use-mobile";
import { DesktopOnlyNotice } from "./mobile/desktop-only-notice";

type SektionId = "s1" | "s2" | "s3" | "s4" | "s5" | "s6" | "s7" | "s8";

const SECTION_LABELS: { id: SektionId; label: string; icon: string }[] = STAMMDATEN.map((s) => ({
  id: s.id as SektionId,
  label: s.titel,
  icon: s.icon,
}));

const STANDARD_UNITS = ["m", "m²", "lfm", "Stk", "kg", "Liter", "Psch", "Std"];

const CSV_TEMPLATE = "gruppe,name,einheit,ep,tag\ns1,Beispiel-Position,m²,42.50,\ns4,Mörtel-Auftrag XY,m²,75,AZ\n";

interface ProjectLvStateRecord {
  qty?: Record<string, number>;
}

interface ParsedCsvRow {
  ok: boolean;
  rowNum: number;
  raw: Record<string, string>;
  data?: InsertLibraryPosition;
  error?: string;
}

const HEADER_KEYS = ["gruppe", "name", "einheit", "ep", "tag"] as const;
type HeaderKey = (typeof HEADER_KEYS)[number];

const REQUIRED_KEYS: HeaderKey[] = ["gruppe", "name", "einheit", "ep"];

const HEADER_LABELS: Record<HeaderKey, string> = {
  gruppe: "Gruppe (s1–s8)",
  name: "Bezeichnung",
  einheit: "Einheit",
  ep: "EP €",
  tag: "Tag (optional)",
};

const HEADER_SYNONYMS: Record<HeaderKey, string[]> = {
  gruppe: ["gruppe", "sektion", "section", "bereich", "kategorie", "abschnitt", "leistungsgruppe", "lg"],
  name: ["name", "bezeichnung", "beschreibung", "position", "positionsname", "leistung", "titel", "text"],
  einheit: ["einheit", "unit", "me", "mengeneinheit", "einh", "einh."],
  ep: ["ep", "preis", "ep €", "ep eur", "ep e", "einzelpreis", "einheitspreis", "preis €", "preis eur", "€", "eur"],
  tag: ["tag", "marker", "kennzeichen", "label", "merkmal", "flag"],
};

interface RawSheet {
  headers: string[];
  rows: string[][];
}

function normalizeHeader(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, " ");
}

const MAPPING_STORAGE_KEY = "positions-library-import-mappings-v1";
const RECENT_MAPPINGS_LIMIT = 20;

interface MappingProfile {
  id: string;
  name: string;
  headers: string[];
  mapping: Record<HeaderKey, number>;
}

interface MappingStore {
  profiles: MappingProfile[];
  recentMappings: Record<string, Record<HeaderKey, number>>;
}

function headersSignature(headers: string[]): string {
  return headers.map(normalizeHeader).join("||");
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parseMapping(value: unknown): Record<HeaderKey, number> | null {
  if (!isPlainObject(value)) return null;
  const result: Record<HeaderKey, number> = { gruppe: -1, name: -1, einheit: -1, ep: -1, tag: -1 };
  for (const key of HEADER_KEYS) {
    const raw = value[key];
    const num = typeof raw === "number" ? raw : Number(raw);
    result[key] = Number.isFinite(num) ? Math.trunc(num) : -1;
  }
  return result;
}

function parseProfile(value: unknown): MappingProfile | null {
  if (!isPlainObject(value)) return null;
  const { id, name, headers, mapping } = value;
  if (typeof id !== "string" || typeof name !== "string" || !Array.isArray(headers)) return null;
  const parsedMapping = parseMapping(mapping);
  if (!parsedMapping) return null;
  return {
    id,
    name,
    headers: headers.map((h) => (typeof h === "string" ? h : String(h ?? ""))),
    mapping: parsedMapping,
  };
}

function loadMappingStore(): MappingStore {
  const empty: MappingStore = { profiles: [], recentMappings: {} };
  if (typeof window === "undefined") return empty;
  try {
    const raw = window.localStorage.getItem(MAPPING_STORAGE_KEY);
    if (!raw) return empty;
    const parsed: unknown = JSON.parse(raw);
    if (!isPlainObject(parsed)) return empty;
    const profilesRaw = Array.isArray(parsed.profiles) ? parsed.profiles : [];
    const profiles: MappingProfile[] = [];
    for (const entry of profilesRaw) {
      const p = parseProfile(entry);
      if (p) profiles.push(p);
    }
    const recentMappings: Record<string, Record<HeaderKey, number>> = {};
    if (isPlainObject(parsed.recentMappings)) {
      for (const [sig, mappingValue] of Object.entries(parsed.recentMappings)) {
        const m = parseMapping(mappingValue);
        if (m) recentMappings[sig] = m;
      }
    }
    return { profiles, recentMappings };
  } catch {
    return empty;
  }
}

function saveMappingStore(store: MappingStore) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(MAPPING_STORAGE_KEY, JSON.stringify(store));
  } catch {
    // ignore quota errors
  }
}

function mappingsEqual(a: Record<HeaderKey, number>, b: Record<HeaderKey, number>): boolean {
  return HEADER_KEYS.every((k) => a[k] === b[k]);
}

function trimRecentMappings(
  recent: Record<string, Record<HeaderKey, number>>,
  preserveKey: string,
): Record<string, Record<HeaderKey, number>> {
  const keys = Object.keys(recent);
  if (keys.length <= RECENT_MAPPINGS_LIMIT) return recent;
  const trimmed: Record<string, Record<HeaderKey, number>> = { [preserveKey]: recent[preserveKey] };
  const others = keys.filter((k) => k !== preserveKey).slice(-(RECENT_MAPPINGS_LIMIT - 1));
  for (const k of others) trimmed[k] = recent[k];
  return trimmed;
}

function autoMap(headers: string[]): Record<HeaderKey, number> {
  const norm = headers.map(normalizeHeader);
  const result: Record<HeaderKey, number> = { gruppe: -1, name: -1, einheit: -1, ep: -1, tag: -1 };
  for (const key of HEADER_KEYS) {
    const synonyms = HEADER_SYNONYMS[key];
    let found = -1;
    for (const syn of synonyms) {
      const i = norm.indexOf(syn);
      if (i !== -1) { found = i; break; }
    }
    if (found === -1) {
      for (const syn of synonyms) {
        const i = norm.findIndex((h) => h.includes(syn));
        if (i !== -1) { found = i; break; }
      }
    }
    result[key] = found;
  }
  return result;
}

function validateRows(
  records: Array<{ rowNum: number; raw: Record<string, string> }>,
  missing: HeaderKey[],
): ParsedCsvRow[] {
  const rows: ParsedCsvRow[] = [];
  for (const { rowNum, raw } of records) {
    if (missing.length > 0) {
      rows.push({ ok: false, rowNum, raw, error: `Fehlende Spalten: ${missing.join(", ")}` });
      continue;
    }
    const gruppe = (raw.gruppe ?? "").toLowerCase();
    if (!/^s[1-8]$/.test(gruppe)) {
      rows.push({ ok: false, rowNum, raw, error: `Ungültige Gruppe „${raw.gruppe}" (erlaubt: s1–s8)` });
      continue;
    }
    if (!raw.name) {
      rows.push({ ok: false, rowNum, raw, error: "Name fehlt" });
      continue;
    }
    if (!raw.einheit) {
      rows.push({ ok: false, rowNum, raw, error: "Einheit fehlt" });
      continue;
    }
    const epNorm = raw.ep.replace(",", ".");
    const epNum = Number(epNorm);
    if (!Number.isFinite(epNum) || epNum < 0) {
      rows.push({ ok: false, rowNum, raw, error: `Ungültiger EP „${raw.ep}"` });
      continue;
    }
    rows.push({
      ok: true,
      rowNum,
      raw,
      data: {
        sektionId: gruppe as SektionId,
        name: raw.name,
        unit: raw.einheit,
        ep: epNum,
        tag: raw.tag || null,
        aktiv: true,
      },
    });
  }
  return rows;
}

function parseCsvRaw(text: string): RawSheet {
  const cleaned = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const lines = cleaned.split("\n").filter((l) => l.trim().length > 0);
  if (lines.length === 0) return { headers: [], rows: [] };
  const splitLine = (line: string): string[] => {
    const out: string[] = [];
    let cur = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') {
          cur += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if ((ch === "," || ch === ";") && !inQuotes) {
        out.push(cur);
        cur = "";
      } else {
        cur += ch;
      }
    }
    out.push(cur);
    return out.map((s) => s.trim());
  };
  const headers = splitLine(lines[0]);
  const rows = lines.slice(1).map((line) => splitLine(line));
  return { headers, rows };
}

function parseXlsxAoaRaw(aoa: unknown[][]): RawSheet {
  const nonEmpty = aoa.filter((r) => r && r.some((c) => String(c ?? "").trim().length > 0));
  if (nonEmpty.length === 0) return { headers: [], rows: [] };
  const headers = (nonEmpty[0] as unknown[]).map((h) => String(h ?? "").trim());
  const rows = nonEmpty.slice(1).map((cells) =>
    (cells as unknown[]).map((v) => (v === undefined || v === null ? "" : String(v).trim())),
  );
  return { headers, rows };
}

async function parseXlsxRaw(buffer: ArrayBuffer): Promise<RawSheet> {
  const XLSX = await import("xlsx");
  const wb = XLSX.read(buffer, { type: "array" });
  const firstSheetName = wb.SheetNames[0];
  if (!firstSheetName) return { headers: [], rows: [] };
  const sheet = wb.Sheets[firstSheetName];
  const aoa = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, blankrows: false, defval: "" });
  return parseXlsxAoaRaw(aoa);
}

function downloadFile(filename: string, content: string, mime: string) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

type StatusFilter = "alle" | "entwurf" | "freigegeben";

export default function PositionsLibraryPage() {
  const isMobile = useIsMobile();
  if (isMobile) {
    return (
      <DesktopOnlyNotice
        title="Positions-Bibliothek"
        description="Die Bibliothek lässt sich am Computer am bequemsten pflegen. Bitte am Desktop öffnen."
      />
    );
  }
  return <DesktopPositionsLibraryPage />;
}

function DesktopPositionsLibraryPage() {
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [tab, setTab] = useState("manage");
  const [takeoverOpen, setTakeoverOpen] = useState<LibraryPosition | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [historyPos, setHistoryPos] = useState<LibraryPosition | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("alle");
  const [searchTerm, setSearchTerm] = useState("");
  const { data: userProjects = [] } = useQuery<Project[]>({ queryKey: ["/api/projects"] });
  const [addForm, setAddForm] = useState<InsertLibraryPosition>({
    sektionId: "s1",
    name: "",
    unit: "m²",
    ep: 0,
    tag: null,
    aktiv: true,
  });

  // Diese Verwaltungs-Seite braucht ALLE Einträge (inkl. Entwürfe). Der
  // Server liefert ohne `status`-Param per Default nur "freigegeben", daher
  // hier explizit `status=alle`.
  const { data: positions = [], isLoading } = useQuery<LibraryPosition[]>({
    queryKey: ["/api/library-positions", "alle"],
    queryFn: async () => {
      const res = await fetch("/api/library-positions?status=alle", { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
  });

  const entwurfAnzahl = useMemo(() => positions.filter((p) => p.status === "entwurf").length, [positions]);

  const trimmedSearch = searchTerm.trim();
  const searchActive = trimmedSearch.length > 0;

  const filteredPositions = useMemo(() => {
    const q = trimmedSearch.toLowerCase();
    return positions.filter((p) => {
      if (statusFilter !== "alle" && p.status !== statusFilter) return false;
      if (!q) return true;
      return (
        p.name.toLowerCase().includes(q) ||
        p.unit.toLowerCase().includes(q) ||
        (p.tag ?? "").toLowerCase().includes(q)
      );
    });
  }, [positions, statusFilter, trimmedSearch]);

  const grouped = useMemo(() => {
    const map = new Map<SektionId, LibraryPosition[]>();
    SECTION_LABELS.forEach((s) => map.set(s.id, []));
    filteredPositions.forEach((p) => {
      const arr = map.get(p.sektionId as SektionId);
      if (arr) arr.push(p);
    });
    return map;
  }, [filteredPositions]);

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ["/api/library-positions"] });
    queryClient.invalidateQueries({ queryKey: ["/api/templates/custom-positions"] });
  }

  const createMutation = useMutation({
    mutationFn: (data: InsertLibraryPosition) =>
      apiRequest("POST", "/api/library-positions", data).then((r) => r.json()),
    onSuccess: () => {
      invalidate();
      setAddOpen(false);
      setAddForm({ sektionId: "s1", name: "", unit: "m²", ep: 0, tag: null, aktiv: true });
      toast({ title: "Position angelegt" });
    },
    onError: (e: Error) => toast({ title: "Fehler", description: e.message, variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<InsertLibraryPosition> }) =>
      apiRequest("PATCH", `/api/library-positions/${id}`, data).then((r) => r.json()),
    onSuccess: () => invalidate(),
    onError: (e: Error) => toast({ title: "Fehler", description: e.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiRequest("DELETE", `/api/library-positions/${id}`),
    onSuccess: () => {
      invalidate();
      setDeleteId(null);
      toast({ title: "Position gelöscht" });
    },
    onError: (e: Error) => toast({ title: "Fehler", description: e.message, variant: "destructive" }),
  });

  // ---- Duplikate ----
  const { data: duplicatesData, isLoading: duplicatesLoading } = useQuery<{ groups: DuplicateGroup[] }>({
    queryKey: ["/api/library-positions/duplicates"],
  });
  const duplicateGroups: DuplicateGroup[] = duplicatesData?.groups ?? [];
  const exactGroupCount = useMemo(
    () => duplicateGroups.filter((g) => g.kind === "exact").length,
    [duplicateGroups],
  );

  const mergeMutation = useMutation({
    mutationFn: (payload: { keepId: number; removeIds: number[] }) =>
      apiRequest("POST", "/api/library-positions/merge", payload).then(
        (r) => r.json() as Promise<{ removedCount: number }>,
      ),
    onSuccess: (res) => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ["/api/library-positions/duplicates"] });
      toast({
        title: "Duplikate zusammengeführt",
        description: `${res.removedCount} Eintrag/Einträge entfernt, Preis-Samples übernommen.`,
      });
    },
    onError: (e: Error) =>
      toast({ title: "Zusammenführen fehlgeschlagen", description: e.message, variant: "destructive" }),
  });

  const bulkMutation = useMutation({
    mutationFn: (items: InsertLibraryPosition[]) =>
      apiRequest("POST", "/api/library-positions/bulk", { items }).then((r) => r.json()),
    onSuccess: (res: { count: number }) => {
      invalidate();
      toast({ title: "Import abgeschlossen", description: `${res.count} Positionen übernommen.` });
    },
    onError: (e: Error) => toast({ title: "Fehler", description: e.message, variant: "destructive" }),
  });

  const approveMutation = useMutation({
    mutationFn: (id: number) => apiRequest("POST", `/api/library-positions/${id}/approve`).then((r) => r.json()),
    onSuccess: () => {
      invalidate();
      toast({ title: "Position freigegeben" });
    },
    onError: (e: Error) => toast({ title: "Fehler", description: e.message, variant: "destructive" }),
  });

  const rejectMutation = useMutation({
    mutationFn: (id: number) => apiRequest("POST", `/api/library-positions/${id}/reject`),
    onSuccess: () => {
      invalidate();
      toast({ title: "Entwurf verworfen" });
    },
    onError: (e: Error) => toast({ title: "Fehler", description: e.message, variant: "destructive" }),
  });

  const extractMutation = useMutation({
    mutationFn: async (payload: { kind: "pdf" | "excel" | "image" | "csv"; fileName: string; mimeType: string; data: string }) => {
      const res = await apiRequest("POST", "/api/library-positions/extract", payload);
      return (await res.json()) as { count: number };
    },
    onSuccess: (res) => {
      invalidate();
      if (res.count === 0) {
        toast({ title: "Keine Positionen erkannt", description: "Die KI konnte keine eindeutigen Positionen extrahieren." });
      } else {
        toast({
          title: `${res.count} Positionen in die Bibliothek übernommen`,
          description: 'Direkt im Reiter „Bibliothek" verfügbar.',
        });
        setStatusFilter("freigegeben");
      }
    },
    onError: (e: Error) => toast({ title: "KI-Upload fehlgeschlagen", description: e.message, variant: "destructive" }),
  });

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-6xl mx-auto">
        <div className="mb-2">
          <Link href="/dashboard">
            <Button variant="ghost" size="sm" className="text-gray-500 hover:text-bauwerk-teal -ml-2">
              <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
            </Button>
          </Link>
        </div>
        <div className="mb-8 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
              <BookOpen className="w-7 h-7 text-bauwerk-teal" />
              Positions-Bibliothek
            </h1>
            <p className="text-gray-600">
              Eigene LV-Positionen pro Leistungsgruppe pflegen. Diese stehen automatisch in jedem Projekt zusätzlich
              zu den fest hinterlegten Stammdaten zur Verfügung.
            </p>
            <Link href="/calculation-templates" className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-bauwerk-teal hover:underline">
              Kalkulationsvorlagen aus LV und K7 verwalten
            </Link>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="manage">Bibliothek</TabsTrigger>
            <TabsTrigger value="drafts" className="relative">
              Entwürfe
              {entwurfAnzahl > 0 && (
                <span className="ml-2 inline-flex items-center justify-center rounded-full bg-amber-500 text-white text-[10px] leading-none px-1.5 py-0.5 min-w-[1.25rem]">
                  {entwurfAnzahl}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="duplicates" className="relative" data-testid="lib-tab-duplicates">
              Duplikate
              {duplicateGroups.length > 0 && (
                <span
                  className={`ml-2 inline-flex items-center justify-center rounded-full text-white text-[10px] leading-none px-1.5 py-0.5 min-w-[1.25rem] ${
                    exactGroupCount > 0 ? "bg-red-500" : "bg-amber-500"
                  }`}
                >
                  {duplicateGroups.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="csv">CSV / Excel</TabsTrigger>
            <TabsTrigger value="json">JSON</TabsTrigger>
            <TabsTrigger value="from-project">Aus Projekt</TabsTrigger>
          </TabsList>

          <TabsContent value="manage" className="space-y-4">
            <KiUploadCard pending={extractMutation.isPending} onUpload={(p) => extractMutation.mutate(p)} />

            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline">{positions.length} gesamt</Badge>
                {entwurfAnzahl > 0 && (
                  <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
                    {entwurfAnzahl} Entwürfe
                  </Badge>
                )}
                <div className="flex items-center gap-1 ml-2">
                  {(["alle", "entwurf", "freigegeben"] as StatusFilter[]).map((f) => (
                    <Button
                      key={f}
                      size="sm"
                      variant={statusFilter === f ? "default" : "outline"}
                      className="h-7 text-xs"
                      onClick={() => setStatusFilter(f)}
                      data-testid={`lib-filter-${f}`}
                    >
                      {f === "alle" ? "Alle" : f === "entwurf" ? "Entwürfe" : "Freigegeben"}
                    </Button>
                  ))}
                </div>
              </div>
              <Dialog open={addOpen} onOpenChange={setAddOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="lib-add">
                    <Plus className="w-4 h-4 mr-2" /> Neue Position
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Neue Bibliothek-Position</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3">
                    <div>
                      <Label>Leistungsgruppe</Label>
                      <Select value={addForm.sektionId} onValueChange={(v) => setAddForm({ ...addForm, sektionId: v as SektionId })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {SECTION_LABELS.map((s) => (
                            <SelectItem key={s.id} value={s.id}>{s.icon} {s.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Bezeichnung</Label>
                      <Input value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} />
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <Label>Einheit</Label>
                        <Input value={addForm.unit} onChange={(e) => setAddForm({ ...addForm, unit: e.target.value })} />
                      </div>
                      <div>
                        <Label>EP €</Label>
                        <Input type="number" step="0.01" value={addForm.ep} onChange={(e) => setAddForm({ ...addForm, ep: Number(e.target.value) || 0 })} />
                      </div>
                      <div>
                        <Label>Tag</Label>
                        <Select value={addForm.tag ?? "__none__"} onValueChange={(v) => setAddForm({ ...addForm, tag: v === "__none__" ? null : v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">– kein –</SelectItem>
                            <SelectItem value="AZ">AZ</SelectItem>
                            <SelectItem value="ALT">ALT</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" onClick={() => setAddOpen(false)}>Abbrechen</Button>
                    <Button
                      onClick={() => {
                        if (!addForm.name.trim() || !addForm.unit.trim()) {
                          toast({ title: "Fehler", description: "Name und Einheit sind erforderlich.", variant: "destructive" });
                          return;
                        }
                        createMutation.mutate(addForm);
                      }}
                      disabled={createMutation.isPending}
                    >
                      {createMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Speichern
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Positionen durchsuchen (Bezeichnung, Einheit, Tag)…"
                className="pl-9 pr-9"
                data-testid="lib-search"
              />
              {searchActive && (
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                  onClick={() => setSearchTerm("")}
                  data-testid="lib-search-clear"
                  title="Suche löschen"
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>

            {isLoading ? (
              <div className="text-center py-10 text-muted-foreground">
                <Loader2 className="w-6 h-6 animate-spin mx-auto" />
              </div>
            ) : filteredPositions.length === 0 && searchActive ? (
              <Card>
                <CardContent className="py-10 text-center space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Keine Positionen gefunden für „{trimmedSearch}".
                  </p>
                  <Button
                    onClick={() => {
                      setAddForm({
                        sektionId: "s1",
                        name: trimmedSearch,
                        unit: "m²",
                        ep: 0,
                        tag: null,
                        aktiv: true,
                      });
                      setAddOpen(true);
                    }}
                    data-testid="lib-add-from-search"
                  >
                    <Plus className="w-4 h-4 mr-2" /> Neue Position
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {SECTION_LABELS.map((sek) => {
                  const items = grouped.get(sek.id) ?? [];
                  if (searchActive && items.length === 0) return null;
                  return (
                    <Card key={sek.id}>
                      <CardHeader className="py-3">
                        <CardTitle className="text-base flex items-center gap-2">
                          <span className="text-xl">{sek.icon}</span>
                          {sek.label}
                          <Badge variant="secondary" className="ml-2">{items.length}</Badge>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {items.length === 0 ? (
                          <p className="text-sm text-muted-foreground italic">Keine Bibliothek-Positionen in dieser Gruppe.</p>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead className="text-[10px] uppercase tracking-wider text-muted-foreground border-b">
                                <tr>
                                  <th className="text-left py-1">Bezeichnung</th>
                                  <th className="text-right py-1 w-20">Einheit</th>
                                  <th className="text-right py-1 w-24">EP €</th>
                                  <th className="text-right py-1 w-16">Tag</th>
                                  <th className="text-center py-1 w-16">Aktiv</th>
                                  <th className="text-right py-1 w-12"></th>
                                </tr>
                              </thead>
                              <tbody>
                                {items.map((p) => (
                                  <LibraryRow
                                    key={p.id}
                                    pos={p}
                                    onSave={(patch) => updateMutation.mutate({ id: p.id, data: patch })}
                                    onDelete={() => setDeleteId(p.id)}
                                    onApprove={() => approveMutation.mutate(p.id)}
                                    onHistory={() => setHistoryPos(p)}
                                    approvePending={approveMutation.isPending}
                                  />
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="csv">
            <CsvImportTab onImport={(items) => bulkMutation.mutate(items)} pending={bulkMutation.isPending} />
          </TabsContent>

          <TabsContent value="drafts" className="space-y-4">
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-base">Entwürfe nach Herkunft</CardTitle>
              </CardHeader>
              <CardContent>
                {(() => {
                  const drafts = positions.filter((p) => p.status === "entwurf");
                  if (drafts.length === 0) {
                    return (
                      <p className="text-sm text-muted-foreground py-4 text-center">
                        Keine offenen Entwürfe. KI-Uploads und Foto-Analysen werden direkt in der Bibliothek freigegeben.
                      </p>
                    );
                  }
                  const QUELLE_LABEL: Record<string, { label: string; icon: string }> = {
                    "ki-foto": { label: "KI – Foto-Analyse", icon: "📷" },
                    "ki-pdf": { label: "KI – PDF-Upload", icon: "📄" },
                    "ki-excel": { label: "KI – Excel/CSV", icon: "📊" },
                    "ki-screenshot": { label: "KI – Screenshot", icon: "🖼" },
                    "import": { label: "Manueller Import", icon: "📥" },
                    "manuell": { label: "Manuell", icon: "✍️" },
                    "projekt": { label: "Aus Projekt", icon: "📁" },
                  };
                  const groups = new Map<string, LibraryPosition[]>();
                  for (const d of drafts) {
                    const key = d.quelle || "manuell";
                    const arr = groups.get(key) ?? [];
                    arr.push(d);
                    groups.set(key, arr);
                  }
                  return (
                    <div className="space-y-4">
                      {Array.from(groups.entries()).map(([quelle, items]) => {
                        const meta = QUELLE_LABEL[quelle] ?? { label: quelle, icon: "•" };
                        return (
                          <div key={quelle}>
                            <div className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                              <span>{meta.icon}</span> {meta.label}
                              <Badge variant="outline" className="ml-1">{items.length}</Badge>
                            </div>
                            <div className="space-y-1.5">
                              {items.map((p) => {
                                const sek = SECTION_LABELS.find((s) => s.id === p.sektionId);
                                return (
                                  <DraftRow
                                    key={p.id}
                                    pos={p}
                                    sektion={sek}
                                    onSave={(patch) => updateMutation.mutate({ id: p.id, data: patch })}
                                    savePending={updateMutation.isPending}
                                    onApprove={() => approveMutation.mutate(p.id)}
                                    approvePending={approveMutation.isPending}
                                    onApproveAndTakeover={() => setTakeoverOpen(p)}
                                    onReject={() => rejectMutation.mutate(p.id)}
                                    rejectPending={rejectMutation.isPending}
                                    onHistory={() => setHistoryPos(p)}
                                  />
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="duplicates" className="space-y-4">
            <DuplicatesTab
              groups={duplicateGroups}
              loading={duplicatesLoading}
              merging={mergeMutation.isPending}
              onMerge={(payload) => mergeMutation.mutate(payload)}
            />
          </TabsContent>

          <TabsContent value="json">
            <JsonImportExportTab positions={positions} onImport={(items) => bulkMutation.mutate(items)} pending={bulkMutation.isPending} />
          </TabsContent>

          <TabsContent value="from-project">
            <FromProjectTab onImport={(items) => bulkMutation.mutate(items)} pending={bulkMutation.isPending} />
          </TabsContent>
        </Tabs>
      </div>

      <PriceHistoryDialog pos={historyPos} onClose={() => setHistoryPos(null)} />

      <Dialog open={takeoverOpen !== null} onOpenChange={(o) => !o && setTakeoverOpen(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Freigeben & in LV übernehmen</DialogTitle>
          </DialogHeader>
          {takeoverOpen && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                „{takeoverOpen.name}" wird freigegeben und erscheint danach im
                LV-Kalkulator des ausgewählten Projekts unter{" "}
                <b>{SECTION_LABELS.find((s) => s.id === takeoverOpen.sektionId)?.label}</b>.
              </p>
              {userProjects.length === 0 ? (
                <div className="text-sm text-muted-foreground">
                  Keine Projekte vorhanden. Bitte zuerst ein Projekt anlegen.
                </div>
              ) : (
                <div className="max-h-72 overflow-y-auto space-y-1">
                  {userProjects.map((pr) => (
                    <Button
                      key={pr.id}
                      variant="outline"
                      className="w-full justify-start h-auto py-2 text-left"
                      data-testid={`takeover-project-${pr.id}`}
                      onClick={async () => {
                        const lib = takeoverOpen;
                        setTakeoverOpen(null);
                        try {
                          await apiRequest("POST", `/api/library-positions/${lib.id}/approve`);
                          await queryClient.invalidateQueries({ queryKey: ["/api/library-positions"] });
                          toast({
                            title: "Freigegeben",
                            description: `"${lib.name}" ist jetzt im LV von ${pr.name} verfügbar.`,
                          });
                          setLocation(`/projekte/${pr.id}`);
                        } catch (e) {
                          toast({
                            title: "Fehler",
                            description: e instanceof Error ? e.message : String(e),
                            variant: "destructive",
                          });
                        }
                      }}
                    >
                      <div>
                        <div className="text-sm font-medium">{pr.name}</div>
                        {(pr.customer || pr.address) && (
                          <div className="text-[11px] text-muted-foreground">
                            {[pr.customer, pr.address].filter(Boolean).join(" · ")}
                          </div>
                        )}
                      </div>
                    </Button>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteId !== null} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Position löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Die Position wird aus der Bibliothek entfernt. Bereits in Projekten gespeicherte Werte bleiben unverändert.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteId !== null && deleteMutation.mutate(deleteId)}>
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const QUELLE_LABEL: Record<string, string> = {
  "manuell": "Manuell",
  "ki-foto": "KI Foto",
  "ki-pdf": "KI PDF",
  "ki-excel": "KI Excel",
  "ki-screenshot": "KI Bild",
  "import": "Import",
  "projekt": "Projekt",
};

function LibraryRow({
  pos,
  onSave,
  onDelete,
  onApprove,
  onHistory,
  approvePending,
}: {
  pos: LibraryPosition;
  onSave: (patch: Partial<InsertLibraryPosition>) => void;
  onDelete: () => void;
  onApprove: () => void;
  onHistory: () => void;
  approvePending: boolean;
}) {
  const [name, setName] = useState(pos.name);
  const [unit, setUnit] = useState(pos.unit);
  const [ep, setEp] = useState(String(pos.ep));
  const [tag, setTag] = useState<string | null>(pos.tag);
  const dirty = name !== pos.name || unit !== pos.unit || Number(ep) !== pos.ep || (tag ?? null) !== (pos.tag ?? null);
  const sampleCount = pos.priceSamples?.length ?? 0;
  const isDraft = pos.status === "entwurf";

  return (
    <tr className={`border-b last:border-0 ${isDraft ? "bg-amber-50/40" : ""}`}>
      <td className="py-1 pr-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} className="h-8 text-sm" />
        <div className="flex items-center gap-1 mt-0.5 flex-wrap">
          {isDraft && (
            <Badge className="h-4 px-1 text-[9px] bg-amber-200 text-amber-900 hover:bg-amber-200">Entwurf</Badge>
          )}
          <Badge variant="outline" className="h-4 px-1 text-[9px]">
            {QUELLE_LABEL[pos.quelle] ?? pos.quelle}
          </Badge>
          {sampleCount > 0 && (
            <button
              type="button"
              className="text-[9px] text-blue-700 underline underline-offset-2 hover:text-blue-900"
              onClick={onHistory}
              data-testid={`lib-history-${pos.id}`}
            >
              {sampleCount} Preis-Samples
            </button>
          )}
        </div>
      </td>
      <td className="py-1 pr-2">
        <Input value={unit} onChange={(e) => setUnit(e.target.value)} className="h-8 text-sm text-right" />
      </td>
      <td className="py-1 pr-2">
        <Input
          type="number"
          step="0.01"
          value={ep}
          onChange={(e) => setEp(e.target.value)}
          className="h-8 text-sm text-right"
        />
      </td>
      <td className="py-1 pr-2">
        <Select value={tag ?? "__none__"} onValueChange={(v) => setTag(v === "__none__" ? null : v)}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">–</SelectItem>
            <SelectItem value="AZ">AZ</SelectItem>
            <SelectItem value="ALT">ALT</SelectItem>
          </SelectContent>
        </Select>
      </td>
      <td className="py-1 text-center">
        <Checkbox
          checked={pos.aktiv}
          onCheckedChange={(c) => onSave({ aktiv: c === true })}
        />
      </td>
      <td className="py-1 text-right">
        <div className="flex items-center justify-end gap-1">
          {sampleCount > 0 && (
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 text-blue-700"
              onClick={onHistory}
              title="Preis-Historie ansehen"
            >
              <History className="w-3.5 h-3.5" />
            </Button>
          )}
          {isDraft && (
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 text-emerald-700"
              onClick={onApprove}
              disabled={approvePending}
              title="Als Wissen freigeben"
              data-testid={`lib-approve-${pos.id}`}
            >
              <CheckCircle2 className="w-4 h-4" />
            </Button>
          )}
          {dirty && (
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 text-emerald-700"
              onClick={() => onSave({ name, unit, ep: Number(ep) || 0, tag })}
              title="Änderungen speichern"
            >
              <Save className="w-3.5 h-3.5" />
            </Button>
          )}
          <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={onDelete} title="Löschen">
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </td>
    </tr>
  );
}

function DraftRow({
  pos,
  sektion,
  onSave,
  savePending,
  onApprove,
  approvePending,
  onApproveAndTakeover,
  onReject,
  rejectPending,
  onHistory,
}: {
  pos: LibraryPosition;
  sektion: { id: string; label: string; icon: string } | undefined;
  onSave: (patch: Partial<InsertLibraryPosition>) => void;
  savePending: boolean;
  onApprove: () => void;
  approvePending: boolean;
  onApproveAndTakeover: () => void;
  onReject: () => void;
  rejectPending: boolean;
  onHistory: () => void;
}) {
  const [name, setName] = useState(pos.name);
  const [unit, setUnit] = useState(pos.unit);
  const [ep, setEp] = useState(String(pos.ep));
  const dirty = name !== pos.name || unit !== pos.unit || Number(ep) !== pos.ep;
  const sampleCount = pos.priceSamples?.length ?? 0;
  return (
    <div
      className="grid grid-cols-12 gap-2 items-start border rounded-md px-3 py-2 bg-amber-50/40 dark:bg-amber-950/20"
      data-testid={`draft-row-${pos.id}`}
    >
      <div className="col-span-6 space-y-1">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => {
            if (name.trim() && name !== pos.name) onSave({ name: name.trim() });
          }}
          className="h-8 text-sm"
          data-testid={`draft-name-${pos.id}`}
        />
        <div className="text-[11px] text-muted-foreground flex items-center gap-2 flex-wrap">
          <span>{sektion?.icon} {sektion?.label}</span>
          {sampleCount > 0 && (
            <button
              type="button"
              className="text-blue-700 underline underline-offset-2 hover:text-blue-900"
              onClick={onHistory}
              data-testid={`draft-history-${pos.id}`}
            >
              {sampleCount} Preis-Samples
            </button>
          )}
        </div>
      </div>
      <div className="col-span-2">
        <Input
          value={unit}
          onChange={(e) => setUnit(e.target.value)}
          onBlur={() => {
            if (unit.trim() && unit !== pos.unit) onSave({ unit: unit.trim() });
          }}
          className="h-8 text-sm"
          data-testid={`draft-unit-${pos.id}`}
        />
      </div>
      <div className="col-span-2">
        <Input
          type="number"
          step="0.01"
          value={ep}
          onChange={(e) => setEp(e.target.value)}
          onBlur={() => {
            const n = Number(ep);
            if (Number.isFinite(n) && n >= 0 && n !== pos.ep) onSave({ ep: n });
          }}
          className="h-8 text-sm text-right"
          data-testid={`draft-ep-${pos.id}`}
        />
      </div>
      <div className="col-span-2 flex flex-wrap gap-1 justify-end">
        <Button
          size="sm"
          className="h-7 bg-green-600 hover:bg-green-700"
          onClick={onApprove}
          disabled={approvePending || dirty}
          title={dirty ? "Erst Änderungen speichern (Feld verlassen)" : "Freigeben"}
          data-testid={`draft-approve-${pos.id}`}
        >
          Freigeben
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-7"
          onClick={onApproveAndTakeover}
          disabled={approvePending || savePending || dirty}
          data-testid={`draft-takeover-${pos.id}`}
        >
          Freigeben & in LV
        </Button>
        <Button
          size="sm"
          variant="destructive"
          className="h-7"
          onClick={onReject}
          disabled={rejectPending}
          data-testid={`draft-reject-${pos.id}`}
        >
          Verwerfen
        </Button>
      </div>
    </div>
  );
}

function KiUploadCard({ pending, onUpload }: {
  pending: boolean;
  onUpload: (p: { kind: "pdf" | "excel" | "image" | "csv"; fileName: string; mimeType: string; data: string }) => void;
}) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  async function handleFile(file: File) {
    const sizeMb = file.size / (1024 * 1024);
    if (sizeMb > 10) {
      toast({ title: "Datei zu groß", description: "Maximal 10 MB.", variant: "destructive" });
      return;
    }
    const lower = file.name.toLowerCase();
    let kind: "pdf" | "excel" | "image" | "csv";
    const mime = file.type || "application/octet-stream";
    if (lower.endsWith(".pdf") || mime === "application/pdf") kind = "pdf";
    else if (lower.endsWith(".xlsx") || lower.endsWith(".xls") || /spreadsheetml|excel/i.test(mime)) kind = "excel";
    else if (lower.endsWith(".csv") || mime === "text/csv") kind = "csv";
    else if (mime.startsWith("image/")) kind = "image";
    else {
      toast({ title: "Format nicht unterstützt", description: "Nur PDF, Excel, CSV, Bilder.", variant: "destructive" });
      return;
    }
    const buf = await file.arrayBuffer();
    const bytes = new Uint8Array(buf);
    let bin = "";
    for (let i = 0; i < bytes.byteLength; i++) bin += String.fromCharCode(bytes[i]);
    const base64 = btoa(bin);
    onUpload({ kind, fileName: file.name, mimeType: mime, data: base64 });
  }

  return (
    <Card className="border-dashed border-2 border-bauwerk-teal/40 bg-bauwerk-teal/5">
      <CardContent className="py-4">
        <div className="flex items-start gap-3 flex-wrap">
          <Sparkles className="w-5 h-5 text-bauwerk-teal mt-0.5" />
          <div className="flex-1 min-w-[240px]">
            <p className="font-semibold text-sm">KI-Upload: LV oder Angebot automatisch einlesen</p>
            <p className="text-xs text-muted-foreground">
              PDF, Excel, CSV oder Screenshot hochladen — die KI legt erkannte Positionen <b>direkt in der Bibliothek</b> an
              (inkl. Mengen-bezogenem Preis-Sample).
            </p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept=".pdf,.xlsx,.xls,.csv,image/*"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleFile(f);
              if (fileInputRef.current) fileInputRef.current.value = "";
            }}
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={pending}
            data-testid="lib-ki-upload"
          >
            {pending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileUp className="w-4 h-4 mr-2" />}
            Datei wählen
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function PriceHistoryDialog({ pos, onClose }: { pos: LibraryPosition | null; onClose: () => void }) {
  const samples: PriceSample[] = pos?.priceSamples ?? [];
  const sorted = [...samples].sort((a, b) => (b.datum ?? "").localeCompare(a.datum ?? ""));
  const nonZero = samples.filter((s) => s.ep > 0);
  const min = nonZero.length > 0 ? Math.min(...nonZero.map((s) => s.ep)) : null;
  const max = nonZero.length > 0 ? Math.max(...nonZero.map((s) => s.ep)) : null;
  const avg = nonZero.length > 0 ? nonZero.reduce((acc, s) => acc + s.ep, 0) / nonZero.length : null;

  return (
    <Dialog open={pos !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <History className="w-4 h-4" />
            Preis-Historie: {pos?.name}
          </DialogTitle>
        </DialogHeader>
        {min !== null && max !== null && avg !== null && (
          <div className="grid grid-cols-3 gap-2 text-sm mb-2">
            <div className="p-2 bg-muted rounded">
              <div className="text-[10px] uppercase text-muted-foreground">Min</div>
              <div className="font-semibold">{min.toFixed(2)} €</div>
            </div>
            <div className="p-2 bg-muted rounded">
              <div className="text-[10px] uppercase text-muted-foreground">Ø</div>
              <div className="font-semibold">{avg.toFixed(2)} €</div>
            </div>
            <div className="p-2 bg-muted rounded">
              <div className="text-[10px] uppercase text-muted-foreground">Max</div>
              <div className="font-semibold">{max.toFixed(2)} €</div>
            </div>
          </div>
        )}
        {sorted.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">Noch keine Preis-Samples für diese Position.</p>
        ) : (
          <div className="max-h-[50vh] overflow-y-auto text-xs">
            <table className="w-full">
              <thead className="text-[10px] uppercase text-muted-foreground border-b">
                <tr>
                  <th className="text-left py-1">Datum</th>
                  <th className="text-right py-1">Menge</th>
                  <th className="text-right py-1">EP €</th>
                  <th className="text-left py-1 pl-2">Quelle</th>
                  <th className="text-left py-1">Notiz</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((s, i) => (
                  <tr key={i} className="border-b last:border-0">
                    <td className="py-1">{s.datum ? new Date(s.datum).toLocaleDateString("de-DE") : "—"}</td>
                    <td className="py-1 text-right">{s.menge > 0 ? s.menge.toLocaleString("de-DE") : "—"} {pos?.unit}</td>
                    <td className="py-1 text-right font-semibold">{s.ep.toFixed(2)}</td>
                    <td className="py-1 pl-2">{QUELLE_LABEL[s.quelle] ?? s.quelle}</td>
                    <td className="py-1 text-muted-foreground">{s.note ?? ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface EditableRow {
  rowNum: number;
  values: Record<HeaderKey, string>;
  excluded: boolean;
}

function buildEditableRows(sheet: RawSheet, mapping: Record<HeaderKey, number>): EditableRow[] {
  return sheet.rows.map((cells, i) => {
    const values: Record<HeaderKey, string> = { gruppe: "", name: "", einheit: "", ep: "", tag: "" };
    HEADER_KEYS.forEach((k) => {
      const idx = mapping[k];
      values[k] = idx >= 0 ? (cells[idx] ?? "") : "";
    });
    return { rowNum: i + 2, values, excluded: false };
  });
}

function CsvImportTab({ onImport, pending }: { onImport: (items: InsertLibraryPosition[]) => void; pending: boolean }) {
  const [sheet, setSheet] = useState<RawSheet | null>(null);
  const [mapping, setMapping] = useState<Record<HeaderKey, number>>({ gruppe: -1, name: -1, einheit: -1, ep: -1, tag: -1 });
  const [fileName, setFileName] = useState<string>("");
  const [parsing, setParsing] = useState(false);
  const [editedRows, setEditedRows] = useState<EditableRow[]>([]);
  const [store, setStore] = useState<MappingStore>(() => loadMappingStore());
  const [activeProfileId, setActiveProfileId] = useState<string | null>(null);
  const [autoApplied, setAutoApplied] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const persistStore = (next: MappingStore) => {
    setStore(next);
    saveMappingStore(next);
  };

  useEffect(() => {
    if (!sheet) {
      setEditedRows([]);
      return;
    }
    setEditedRows(buildEditableRows(sheet, mapping));
  }, [sheet, mapping]);

  const validatedRows = useMemo(() => {
    return editedRows.map((r) => {
      const parsed = validateRows([{ rowNum: r.rowNum, raw: r.values }], [])[0];
      return { ...parsed, excluded: r.excluded, values: r.values };
    });
  }, [editedRows]);

  const valid = validatedRows.filter((r) => r.ok && !r.excluded);
  const invalid = validatedRows.filter((r) => !r.ok && !r.excluded);
  const excludedCount = validatedRows.filter((r) => r.excluded).length;
  const missingRequired = REQUIRED_KEYS.filter((k) => mapping[k] < 0);

  const updateRow = (rowNum: number, key: HeaderKey, value: string) => {
    setEditedRows((prev) =>
      prev.map((r) => (r.rowNum === rowNum ? { ...r, values: { ...r.values, [key]: value } } : r)),
    );
  };

  const toggleExclude = (rowNum: number) => {
    setEditedRows((prev) => prev.map((r) => (r.rowNum === rowNum ? { ...r, excluded: !r.excluded } : r)));
  };

  const resetRow = (rowNum: number) => {
    if (!sheet) return;
    const idx = rowNum - 2;
    const cells = sheet.rows[idx];
    if (!cells) return;
    const values: Record<HeaderKey, string> = { gruppe: "", name: "", einheit: "", ep: "", tag: "" };
    HEADER_KEYS.forEach((k) => {
      const i = mapping[k];
      values[k] = i >= 0 ? (cells[i] ?? "") : "";
    });
    setEditedRows((prev) => prev.map((r) => (r.rowNum === rowNum ? { ...r, values, excluded: false } : r)));
  };
  const duplicateKeys = useMemo(() => {
    const counts = new Map<number, HeaderKey[]>();
    HEADER_KEYS.forEach((k) => {
      const idx = mapping[k];
      if (idx < 0) return;
      const arr = counts.get(idx) ?? [];
      arr.push(k);
      counts.set(idx, arr);
    });
    const dup: HeaderKey[] = [];
    counts.forEach((keys) => {
      if (keys.length > 1) dup.push(...keys);
    });
    return dup;
  }, [mapping]);

  const reset = () => {
    setSheet(null);
    setMapping({ gruppe: -1, name: -1, einheit: -1, ep: -1, tag: -1 });
    setFileName("");
    setActiveProfileId(null);
    setAutoApplied(null);
  };

  const onSheetLoaded = (s: RawSheet) => {
    setSheet(s);
    const sig = headersSignature(s.headers);
    const matchingProfile = store.profiles.find((p) => headersSignature(p.headers) === sig);
    if (matchingProfile) {
      setMapping(matchingProfile.mapping);
      setActiveProfileId(matchingProfile.id);
      setAutoApplied(`Profil „${matchingProfile.name}"`);
      return;
    }
    const recent = store.recentMappings[sig];
    if (recent) {
      setMapping(recent);
      setActiveProfileId(null);
      setAutoApplied("Zuletzt verwendete Zuordnung");
      return;
    }
    setMapping(autoMap(s.headers));
    setActiveProfileId(null);
    setAutoApplied(null);
  };

  useEffect(() => {
    if (!sheet) return;
    const sig = headersSignature(sheet.headers);
    if (!sig) return;
    const hasAnyMapping = HEADER_KEYS.some((k) => mapping[k] >= 0);
    if (!hasAnyMapping) return;
    const existing = store.recentMappings[sig];
    if (existing && mappingsEqual(existing, mapping)) return;
    const nextRecent = trimRecentMappings({ ...store.recentMappings, [sig]: { ...mapping } }, sig);
    persistStore({ ...store, recentMappings: nextRecent });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapping, sheet]);

  const handleApplyProfile = (profileId: string) => {
    const profile = store.profiles.find((p) => p.id === profileId);
    if (!profile) return;
    setMapping(profile.mapping);
    setActiveProfileId(profile.id);
    setAutoApplied(null);
  };

  const handleSaveProfile = () => {
    if (!sheet) return;
    const defaultName = activeProfileId
      ? store.profiles.find((p) => p.id === activeProfileId)?.name ?? ""
      : fileName.replace(/\.[^.]+$/, "") || "Neues Profil";
    const name = window.prompt("Name für die Spaltenzuordnung:", defaultName);
    if (!name) return;
    const trimmed = name.trim();
    if (!trimmed) return;
    const existing = store.profiles.find((p) => p.name.toLowerCase() === trimmed.toLowerCase());
    const profile: MappingProfile = {
      id: existing?.id ?? `mp_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
      name: trimmed,
      headers: sheet.headers.slice(),
      mapping: { ...mapping },
    };
    const profiles = existing
      ? store.profiles.map((p) => (p.id === existing.id ? profile : p))
      : [...store.profiles, profile];
    persistStore({ ...store, profiles });
    setActiveProfileId(profile.id);
    setAutoApplied(null);
    toast({
      title: existing ? "Zuordnung aktualisiert" : "Zuordnung gespeichert",
      description: `Profil „${trimmed}" steht für künftige Importe bereit.`,
    });
  };

  const handleDeleteProfile = () => {
    if (!activeProfileId) return;
    const profile = store.profiles.find((p) => p.id === activeProfileId);
    if (!profile) return;
    if (!window.confirm(`Profil „${profile.name}" wirklich löschen?`)) return;
    const profiles = store.profiles.filter((p) => p.id !== activeProfileId);
    persistStore({ ...store, profiles });
    setActiveProfileId(null);
    toast({ title: "Profil gelöscht", description: `„${profile.name}" wurde entfernt.` });
  };

  const handleFile = (file: File) => {
    setFileName(file.name);
    const lower = file.name.toLowerCase();
    const isXlsx = lower.endsWith(".xlsx") || lower.endsWith(".xlsm") || lower.endsWith(".xls");
    const reader = new FileReader();
    if (isXlsx) {
      setParsing(true);
      reader.onload = async () => {
        try {
          const buf = reader.result as ArrayBuffer;
          const parsed = await parseXlsxRaw(buf);
          onSheetLoaded(parsed);
        } catch (err) {
          setSheet(null);
          toast({
            title: "Excel-Datei konnte nicht gelesen werden",
            description: err instanceof Error ? err.message : String(err),
            variant: "destructive",
          });
        } finally {
          setParsing(false);
        }
      };
      reader.readAsArrayBuffer(file);
    } else {
      reader.onload = () => {
        const text = String(reader.result ?? "");
        onSheetLoaded(parseCsvRaw(text));
      };
      reader.readAsText(file, "utf-8");
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">CSV- / Excel-Import</CardTitle>
        <CardDescription>
          Nach dem Datei-Upload können die Spalten den Pflichtfeldern (Gruppe, Bezeichnung, Einheit, EP) und dem optionalen Tag zugeordnet werden.
          Häufige Bezeichnungen wie „Bezeichnung", „Preis" oder „Sektion" werden automatisch erkannt.
          Unterstützte Formate: <code>.csv</code>, <code>.xlsx</code> (erste Tabellenseite wird gelesen).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <input
            ref={inputRef}
            type="file"
            accept=".csv,.xlsx,.xlsm,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
              e.target.value = "";
            }}
          />
          <Button variant="outline" onClick={() => inputRef.current?.click()} disabled={parsing} data-testid="lib-import-file">
            {parsing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            CSV / Excel wählen
          </Button>
          <Button
            variant="outline"
            onClick={() => downloadFile("positions-bibliothek-vorlage.csv", CSV_TEMPLATE, "text/csv;charset=utf-8")}
          >
            <FileText className="w-4 h-4 mr-2" /> Vorlage herunterladen
          </Button>
          {fileName && <span className="text-sm text-muted-foreground">{fileName}</span>}
          {sheet && (
            <Button variant="ghost" size="sm" onClick={reset}>
              Zurücksetzen
            </Button>
          )}
        </div>

        {sheet && (
          <div className="space-y-2 border rounded p-3 bg-muted/20">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-sm font-medium">Spaltenzuordnung</div>
              <div className="flex items-center gap-2 flex-wrap">
                {store.profiles.length > 0 && (
                  <div className="flex items-center gap-1">
                    <Label className="text-xs text-muted-foreground">Profil:</Label>
                    <Select
                      value={activeProfileId ?? "__none__"}
                      onValueChange={(v) => {
                        if (v === "__none__") {
                          setActiveProfileId(null);
                          setAutoApplied(null);
                        } else {
                          handleApplyProfile(v);
                        }
                      }}
                    >
                      <SelectTrigger className="h-8 text-xs w-[180px]" data-testid="lib-import-profile">
                        <SelectValue placeholder="– kein Profil –" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">– kein Profil –</SelectItem>
                        {store.profiles.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {activeProfileId && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={handleDeleteProfile}
                        title="Profil löschen"
                        data-testid="lib-import-profile-delete"
                      >
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={handleSaveProfile}
                  data-testid="lib-import-profile-save"
                >
                  <BookmarkPlus className="w-3.5 h-3.5 mr-1.5" />
                  Zuordnung speichern
                </Button>
              </div>
            </div>
            {autoApplied && (
              <p className="text-xs text-emerald-700">
                {autoApplied} wurde automatisch angewendet.
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              Erkannte Spaltenüberschriften: {sheet.headers.length === 0 ? "–" : sheet.headers.map((h, i) => `${i + 1}. ${h || "(leer)"}`).join(", ")}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
              {HEADER_KEYS.map((key) => {
                const isRequired = REQUIRED_KEYS.includes(key);
                return (
                  <div key={key} className="space-y-1">
                    <Label className="text-xs">
                      {HEADER_LABELS[key]}
                      {isRequired && <span className="text-destructive ml-0.5">*</span>}
                    </Label>
                    <Select
                      value={String(mapping[key])}
                      onValueChange={(v) => {
                        setMapping((prev) => ({ ...prev, [key]: Number(v) }));
                        setAutoApplied(null);
                      }}
                    >
                      <SelectTrigger className="h-8 text-xs" data-testid={`map-${key}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="-1">– keine –</SelectItem>
                        {sheet.headers.map((h, i) => (
                          <SelectItem key={i} value={String(i)}>
                            {h || `Spalte ${i + 1}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                );
              })}
            </div>
            {missingRequired.length > 0 && (
              <p className="text-xs text-destructive">
                Bitte allen Pflichtfeldern eine Spalte zuordnen: {missingRequired.map((k) => HEADER_LABELS[k]).join(", ")}
              </p>
            )}
            {duplicateKeys.length > 0 && (
              <p className="text-xs text-amber-700">
                Hinweis: Mehrere Felder verweisen auf dieselbe Quellspalte ({duplicateKeys.map((k) => HEADER_LABELS[k]).join(", ")}). Bitte prüfen.
              </p>
            )}
          </div>
        )}

        {validatedRows.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm flex-wrap">
              <Badge variant="outline" className="text-emerald-700 border-emerald-300">
                {valid.length} gültig
              </Badge>
              {invalid.length > 0 && (
                <Badge variant="outline" className="text-destructive border-destructive">
                  {invalid.length} mit Fehlern
                </Badge>
              )}
              {excludedCount > 0 && (
                <Badge variant="outline" className="text-muted-foreground">
                  {excludedCount} ausgeschlossen
                </Badge>
              )}
              <span className="text-xs text-muted-foreground">
                Werte können direkt in der Tabelle korrigiert werden – die Validierung läuft live mit.
              </span>
            </div>
            <div className="max-h-96 overflow-auto border rounded">
              <table className="w-full text-xs">
                <thead className="bg-muted/40 sticky top-0">
                  <tr>
                    <th className="text-left p-1.5 w-10">Zeile</th>
                    <th className="text-left p-1.5 w-28">Gruppe</th>
                    <th className="text-left p-1.5">Bezeichnung</th>
                    <th className="text-left p-1.5 w-24">Einheit</th>
                    <th className="text-right p-1.5 w-20">EP €</th>
                    <th className="text-left p-1.5 w-24">Tag</th>
                    <th className="text-left p-1.5 w-44">Status</th>
                    <th className="text-right p-1.5 w-20"></th>
                  </tr>
                </thead>
                <tbody>
                  {validatedRows.map((r) => {
                    const rowClass = r.excluded
                      ? "bg-muted/40 text-muted-foreground"
                      : r.ok
                        ? "bg-emerald-50/60"
                        : "bg-red-50";
                    return (
                      <tr key={r.rowNum} className={`border-t ${rowClass}`}>
                        <td className="p-1.5 text-muted-foreground align-middle">{r.rowNum}</td>
                        <td className="p-1.5">
                          <Select
                            value={SECTION_LABELS.some((s) => s.id === r.values.gruppe.toLowerCase()) ? r.values.gruppe.toLowerCase() : "__none__"}
                            onValueChange={(v) => updateRow(r.rowNum, "gruppe", v === "__none__" ? "" : v)}
                            disabled={r.excluded}
                          >
                            <SelectTrigger className="h-7 text-xs">
                              <SelectValue placeholder={r.values.gruppe || "–"} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">– wählen –</SelectItem>
                              {SECTION_LABELS.map((s) => (
                                <SelectItem key={s.id} value={s.id}>
                                  {s.id} · {s.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="p-1.5">
                          <Input
                            value={r.values.name}
                            onChange={(e) => updateRow(r.rowNum, "name", e.target.value)}
                            disabled={r.excluded}
                            className="h-7 text-xs"
                          />
                        </td>
                        <td className="p-1.5">
                          <Input
                            value={r.values.einheit}
                            onChange={(e) => updateRow(r.rowNum, "einheit", e.target.value)}
                            disabled={r.excluded}
                            className="h-7 text-xs"
                            list={`units-row-${r.rowNum}`}
                          />
                          <datalist id={`units-row-${r.rowNum}`}>
                            {STANDARD_UNITS.map((u) => (
                              <option key={u} value={u} />
                            ))}
                          </datalist>
                        </td>
                        <td className="p-1.5">
                          <Input
                            value={r.values.ep}
                            onChange={(e) => updateRow(r.rowNum, "ep", e.target.value)}
                            disabled={r.excluded}
                            className="h-7 text-xs text-right"
                            inputMode="decimal"
                          />
                        </td>
                        <td className="p-1.5">
                          <Select
                            value={r.values.tag === "AZ" || r.values.tag === "ALT" ? r.values.tag : "__none__"}
                            onValueChange={(v) => updateRow(r.rowNum, "tag", v === "__none__" ? "" : v)}
                            disabled={r.excluded}
                          >
                            <SelectTrigger className="h-7 text-xs">
                              <SelectValue placeholder={r.values.tag || "–"} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">–</SelectItem>
                              <SelectItem value="AZ">AZ</SelectItem>
                              <SelectItem value="ALT">ALT</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="p-1.5 align-middle">
                          {r.excluded ? (
                            <span className="text-muted-foreground italic">ausgeschlossen</span>
                          ) : r.ok ? (
                            <span className="text-emerald-700">OK</span>
                          ) : (
                            <span className="text-destructive">{r.error}</span>
                          )}
                        </td>
                        <td className="p-1.5 text-right align-middle">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-6 w-6"
                              onClick={() => resetRow(r.rowNum)}
                              title="Zeile auf Original zurücksetzen"
                              data-testid={`row-reset-${r.rowNum}`}
                            >
                              <RotateCcw className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className={`h-6 w-6 ${r.excluded ? "text-emerald-700" : "text-destructive"}`}
                              onClick={() => toggleExclude(r.rowNum)}
                              title={r.excluded ? "Zeile wieder aufnehmen" : "Zeile ausschließen"}
                              data-testid={`row-exclude-${r.rowNum}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end">
              <Button
                disabled={valid.length === 0 || pending}
                onClick={() => {
                  onImport(valid.map((r) => r.data!));
                  reset();
                }}
              >
                {pending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {valid.length} Position(en) übernehmen
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function JsonImportExportTab({
  positions,
  onImport,
  pending,
}: {
  positions: LibraryPosition[];
  onImport: (items: InsertLibraryPosition[]) => void;
  pending: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const handleExport = () => {
    const data = {
      libraryPositions: positions,
      exportDate: new Date().toISOString(),
    };
    downloadFile(
      `positions-bibliothek-${new Date().toISOString().split("T")[0]}.json`,
      JSON.stringify(data, null, 2),
      "application/json",
    );
  };

  const handleImport = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const obj = JSON.parse(String(reader.result ?? ""));
        const items = Array.isArray(obj) ? obj : obj.libraryPositions || obj.items;
        if (!Array.isArray(items)) throw new Error("JSON enthält kein Array.");
        const valid: InsertLibraryPosition[] = [];
        for (const it of items) {
          const sektionId = it.sektionId || it.gruppe;
          if (!/^s[1-8]$/.test(sektionId)) continue;
          if (!it.name || !it.unit) continue;
          const ep = Number(it.ep);
          if (!Number.isFinite(ep) || ep < 0) continue;
          valid.push({
            sektionId,
            name: String(it.name),
            unit: String(it.unit),
            ep,
            tag: it.tag || null,
            aktiv: it.aktiv !== false,
          });
        }
        if (valid.length === 0) {
          toast({ title: "Import fehlgeschlagen", description: "Keine gültigen Einträge gefunden.", variant: "destructive" });
          return;
        }
        onImport(valid);
      } catch (e) {
        toast({ title: "Import fehlgeschlagen", description: (e as Error).message, variant: "destructive" });
      }
    };
    reader.readAsText(file, "utf-8");
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">JSON-Import / -Export</CardTitle>
        <CardDescription>Backup oder Übertragung der Bibliothek zwischen Installationen.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={handleExport} disabled={positions.length === 0}>
            <Download className="w-4 h-4 mr-2" /> JSON exportieren ({positions.length})
          </Button>
          <input
            ref={inputRef}
            type="file"
            accept=".json,application/json"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleImport(f);
              e.target.value = "";
            }}
          />
          <Button variant="outline" onClick={() => inputRef.current?.click()} disabled={pending}>
            {pending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            JSON importieren
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function FromProjectTab({ onImport, pending }: { onImport: (items: InsertLibraryPosition[]) => void; pending: boolean }) {
  const [selectedProjectId, setSelectedProjectId] = useState<string>("");
  const [chosen, setChosen] = useState<Set<string>>(new Set());

  const { data: projects = [] } = useQuery<Project[]>({ queryKey: ["/api/projects"] });
  const { data: project } = useQuery<Project>({
    queryKey: ["/api/projects", selectedProjectId],
    enabled: !!selectedProjectId,
  });

  const candidates = useMemo<LVPosition[]>(() => {
    if (!project?.lvState) return [];
    const lv = project.lvState as ProjectLvStateRecord;
    const qty = lv.qty ?? {};
    const stammIds = new Set<string>();
    STAMMDATEN.forEach((s) => s.positionen.forEach((p) => stammIds.add(p.id)));
    const out: LVPosition[] = [];
    Object.keys(qty).forEach((id) => {
      if (stammIds.has(id)) return;
      if (id.startsWith("lib-")) return;
      out.push({
        id,
        name: id,
        unit: "Stk",
        ep: 0,
        qty: qty[id],
        aktiv: true,
      });
    });
    return out;
  }, [project]);

  const sektionFromId = (id: string): SektionId => {
    const m = id.match(/^(?:custom|dup)-(s[1-7])-/);
    if (m) return m[1] as SektionId;
    return "s7";
  };

  const toggle = (id: string) => {
    setChosen((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleImport = () => {
    const items: InsertLibraryPosition[] = candidates
      .filter((c) => chosen.has(c.id))
      .map((c) => ({
        sektionId: sektionFromId(c.id),
        name: c.name,
        unit: c.unit,
        ep: c.ep,
        tag: c.tag ?? null,
        aktiv: true,
      }));
    if (items.length > 0) {
      onImport(items);
      setChosen(new Set());
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Aus bestehendem Projekt importieren</CardTitle>
        <CardDescription>
          Übernimmt eigene Positionen (alles, was nicht zu den fest hinterlegten Stammdaten gehört) aus dem ausgewählten Projekt.
          Hinweis: Da Projekte keine kompletten Positions-Stammdaten speichern, werden Name/Einheit/EP der eigenen Positionen
          aus dem Projekt nur dann sinnvoll übernommen, wenn das Projekt diese seit dem letzten Speichern enthält.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label>Projekt</Label>
          <Select value={selectedProjectId} onValueChange={(v) => { setSelectedProjectId(v); setChosen(new Set()); }}>
            <SelectTrigger><SelectValue placeholder="Projekt wählen" /></SelectTrigger>
            <SelectContent>
              {projects.map((p) => (
                <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {selectedProjectId && (
          candidates.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">Keine importierbaren Eigen-Positionen in diesem Projekt gefunden.</p>
          ) : (
            <>
              <div className="border rounded max-h-80 overflow-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 text-xs">
                    <tr>
                      <th className="p-1.5 w-8"></th>
                      <th className="text-left p-1.5">ID / Name</th>
                      <th className="text-right p-1.5 w-20">Gruppe</th>
                      <th className="text-right p-1.5 w-20">Einheit</th>
                      <th className="text-right p-1.5 w-20">EP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {candidates.map((c) => (
                      <tr key={c.id} className="border-t">
                        <td className="p-1.5">
                          <Checkbox checked={chosen.has(c.id)} onCheckedChange={() => toggle(c.id)} />
                        </td>
                        <td className="p-1.5">{c.name}</td>
                        <td className="p-1.5 text-right">{sektionFromId(c.id)}</td>
                        <td className="p-1.5 text-right">{c.unit}</td>
                        <td className="p-1.5 text-right">{c.ep.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end">
                <Button onClick={handleImport} disabled={chosen.size === 0 || pending}>
                  {pending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {chosen.size} Position(en) übernehmen
                </Button>
              </div>
            </>
          )
        )}
      </CardContent>
    </Card>
  );
}

// ----------------------------------------------------------------------
// Duplicates Tab
// ----------------------------------------------------------------------

interface DuplicateGroupSelection {
  /** Aktuell als Master gewählter Eintrag pro Gruppe. */
  masterId: number;
  /** Zur Entfernung markierte IDs. */
  removeIds: Set<number>;
}

function DuplicatesTab({
  groups,
  loading,
  merging,
  onMerge,
}: {
  groups: DuplicateGroup[];
  loading: boolean;
  merging: boolean;
  onMerge: (payload: { keepId: number; removeIds: number[] }) => void;
}) {
  const [selections, setSelections] = useState<Map<string, DuplicateGroupSelection>>(new Map());
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);

  // Vorbelegung der Selektion immer dann, wenn neue Gruppen-IDs auftauchen.
  // Master = suggestedMasterId, removeIds = alle anderen Eintraege der Gruppe,
  // sofern der Eintrag entfernt werden DARF (kein globaler Eintrag, userId !== null).
  useEffect(() => {
    setSelections((prev) => {
      const next = new Map(prev);
      const validIds = new Set(groups.map((g) => g.id));
      for (const id of Array.from(next.keys())) {
        if (!validIds.has(id)) next.delete(id);
      }
      for (const g of groups) {
        if (next.has(g.id)) continue;
        const removable = new Set<number>();
        for (const e of g.entries) {
          if (e.position.id === g.suggestedMasterId) continue;
          if (e.position.userId === null) continue;
          removable.add(e.position.id);
        }
        next.set(g.id, { masterId: g.suggestedMasterId, removeIds: removable });
      }
      return next;
    });
  }, [groups]);

  const exactGroups = groups.filter((g) => g.kind === "exact");
  const possibleGroups = groups.filter((g) => g.kind === "possible");

  const setMaster = (groupId: string, masterId: number) => {
    setSelections((prev) => {
      const next = new Map(prev);
      const sel = next.get(groupId);
      const group = groups.find((g) => g.id === groupId);
      if (!sel || !group) return prev;
      const removable = new Set<number>();
      for (const e of group.entries) {
        if (e.position.id === masterId) continue;
        if (e.position.userId === null) continue;
        removable.add(e.position.id);
      }
      next.set(groupId, { masterId, removeIds: removable });
      return next;
    });
  };

  const toggleRemove = (groupId: string, posId: number) => {
    setSelections((prev) => {
      const next = new Map(prev);
      const sel = next.get(groupId);
      if (!sel) return prev;
      const newRemove = new Set(sel.removeIds);
      if (newRemove.has(posId)) {
        newRemove.delete(posId);
      } else {
        newRemove.add(posId);
      }
      next.set(groupId, { ...sel, removeIds: newRemove });
      return next;
    });
  };

  const setAllRemoved = (groupId: string, value: boolean) => {
    setSelections((prev) => {
      const next = new Map(prev);
      const sel = next.get(groupId);
      const group = groups.find((g) => g.id === groupId);
      if (!sel || !group) return prev;
      const newRemove = new Set<number>();
      if (value) {
        for (const e of group.entries) {
          if (e.position.id === sel.masterId) continue;
          if (e.position.userId === null) continue;
          newRemove.add(e.position.id);
        }
      }
      next.set(groupId, { ...sel, removeIds: newRemove });
      return next;
    });
  };

  const mergeGroup = (group: DuplicateGroup) => {
    const sel = selections.get(group.id);
    if (!sel) return;
    const removeIds = Array.from(sel.removeIds).filter((id) => id !== sel.masterId);
    if (removeIds.length === 0) return;
    onMerge({ keepId: sel.masterId, removeIds });
  };

  const removeAllExact = () => {
    for (const g of exactGroups) {
      const sel = selections.get(g.id);
      if (!sel) continue;
      const removeIds = Array.from(sel.removeIds).filter((id) => id !== sel.masterId);
      if (removeIds.length === 0) continue;
      onMerge({ keepId: sel.masterId, removeIds });
    }
    setBulkConfirmOpen(false);
  };

  if (loading) {
    return (
      <div className="text-center py-10 text-muted-foreground">
        <Loader2 className="w-6 h-6 animate-spin mx-auto" />
        <p className="text-sm mt-2">Duplikate werden ermittelt …</p>
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <Card>
        <CardContent className="py-10 text-center space-y-2">
          <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto" />
          <p className="text-base font-medium">Keine Duplikate gefunden.</p>
          <p className="text-sm text-muted-foreground">
            Deine Bibliothek ist sauber – keine doppelten Bezeichnungen erkannt.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="py-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Copy className="w-4 h-4" />
                Duplikate bereinigen
              </CardTitle>
              <CardDescription className="mt-1">
                Erkannte doppelte oder ähnliche Einträge zusammenführen. Der Master-Eintrag
                übernimmt die Preis-Samples; entfernte Einträge werden auf <i>inaktiv</i>{" "}
                gesetzt (Soft-Delete) – bestehende Projekte bleiben unverändert.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className="border-red-300 text-red-700">
                {exactGroups.length} exakt
              </Badge>
              <Badge variant="outline" className="border-amber-300 text-amber-700">
                {possibleGroups.length} möglich
              </Badge>
              {exactGroups.length > 0 && (
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => setBulkConfirmOpen(true)}
                  disabled={merging}
                  data-testid="lib-dup-bulk-exact"
                >
                  Alle exakten Duplikate entfernen
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {exactGroups.length > 0 && (
        <DuplicateGroupList
          title="Exakte Duplikate"
          subtitle="Gleiche Bezeichnung, Einheit, Sektion und EP."
          icon={<Copy className="w-4 h-4 text-red-600" />}
          groups={exactGroups}
          selections={selections}
          merging={merging}
          onSetMaster={setMaster}
          onToggleRemove={toggleRemove}
          onSetAllRemoved={setAllRemoved}
          onMergeGroup={mergeGroup}
        />
      )}

      {possibleGroups.length > 0 && (
        <DuplicateGroupList
          title="Mögliche Duplikate"
          subtitle="Gleiche Bezeichnung & Einheit, aber abweichender EP (≤ 10 %) oder leichte Schreibvarianten – bitte einzeln prüfen."
          icon={<AlertTriangle className="w-4 h-4 text-amber-600" />}
          groups={possibleGroups}
          selections={selections}
          merging={merging}
          onSetMaster={setMaster}
          onToggleRemove={toggleRemove}
          onSetAllRemoved={setAllRemoved}
          onMergeGroup={mergeGroup}
        />
      )}

      <AlertDialog open={bulkConfirmOpen} onOpenChange={setBulkConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Alle exakten Duplikate entfernen?</AlertDialogTitle>
            <AlertDialogDescription>
              In {exactGroups.length} Gruppe(n) werden insgesamt{" "}
              {exactGroups.reduce(
                (acc, g) => acc + (selections.get(g.id)?.removeIds.size ?? 0),
                0,
              )}{" "}
              Eintrag/Einträge entfernt. Preis-Samples werden in den jeweils
              vorgeschlagenen Master übernommen. Globale Standard-Einträge
              bleiben immer erhalten.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={removeAllExact}
              data-testid="lib-dup-bulk-exact-confirm"
            >
              Entfernen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function DuplicateGroupList({
  title,
  subtitle,
  icon,
  groups,
  selections,
  merging,
  onSetMaster,
  onToggleRemove,
  onSetAllRemoved,
  onMergeGroup,
}: {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  groups: DuplicateGroup[];
  selections: Map<string, DuplicateGroupSelection>;
  merging: boolean;
  onSetMaster: (groupId: string, masterId: number) => void;
  onToggleRemove: (groupId: string, posId: number) => void;
  onSetAllRemoved: (groupId: string, value: boolean) => void;
  onMergeGroup: (group: DuplicateGroup) => void;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        {icon}
        <h2 className="text-sm font-semibold">{title}</h2>
        <Badge variant="outline">{groups.length}</Badge>
      </div>
      <p className="text-xs text-muted-foreground -mt-1">{subtitle}</p>
      {groups.map((g) => (
        <DuplicateGroupCard
          key={g.id}
          group={g}
          selection={selections.get(g.id)}
          merging={merging}
          onSetMaster={(masterId) => onSetMaster(g.id, masterId)}
          onToggleRemove={(posId) => onToggleRemove(g.id, posId)}
          onSetAllRemoved={(value) => onSetAllRemoved(g.id, value)}
          onMergeGroup={() => onMergeGroup(g)}
        />
      ))}
    </div>
  );
}

function formatDupDate(value: Date | string | null | undefined): string {
  if (!value) return "–";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "–";
  return d.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function DuplicateGroupCard({
  group,
  selection,
  merging,
  onSetMaster,
  onToggleRemove,
  onSetAllRemoved,
  onMergeGroup,
}: {
  group: DuplicateGroup;
  selection: DuplicateGroupSelection | undefined;
  merging: boolean;
  onSetMaster: (masterId: number) => void;
  onToggleRemove: (posId: number) => void;
  onSetAllRemoved: (value: boolean) => void;
  onMergeGroup: () => void;
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const sektion = SECTION_LABELS.find((s) => s.id === group.sektionId);
  const epRange =
    group.epMin === group.epMax
      ? `${group.epMin.toFixed(2)} €`
      : `${group.epMin.toFixed(2)} – ${group.epMax.toFixed(2)} €`;
  const masterId = selection?.masterId ?? group.suggestedMasterId;
  const masterEntry = group.entries.find((e) => e.position.id === masterId);
  const removeIdSet = selection
    ? new Set(Array.from(selection.removeIds).filter((id) => id !== masterId))
    : new Set<number>();
  const removeEntries = group.entries.filter((e) => removeIdSet.has(e.position.id));
  const removeCount = removeEntries.length;
  const removableCount = group.entries.filter(
    (e) => e.position.id !== masterId && e.position.userId !== null,
  ).length;
  const allRemovableSelected =
    removableCount > 0 && removeCount === removableCount;
  const mergedSampleCount =
    (masterEntry?.sampleCount ?? 0) +
    removeEntries.reduce((sum, e) => sum + e.sampleCount, 0);

  return (
    <Card data-testid={`dup-group-${group.id}`}>
      <CardHeader className="py-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <CardTitle className="text-sm flex items-center gap-2 flex-wrap">
              <span className="text-base">{sektion?.icon}</span>
              <span className="truncate">{group.name}</span>
              <Badge variant="outline" className="text-[10px]">{group.unit}</Badge>
            </CardTitle>
            <CardDescription className="text-xs mt-1">
              {sektion?.label} · {group.entries.length} Einträge · EP {epRange}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {removableCount > 0 && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onSetAllRemoved(!allRemovableSelected)}
                disabled={merging}
                data-testid={`dup-select-all-${group.id}`}
              >
                {allRemovableSelected
                  ? "Auswahl leeren"
                  : `Alle ${removableCount} entfernen`}
              </Button>
            )}
            <Button
              size="sm"
              onClick={() => setConfirmOpen(true)}
              disabled={merging || removeCount === 0}
              data-testid={`dup-merge-${group.id}`}
            >
              {merging && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
              {removeCount === 0
                ? "Nichts ausgewählt"
                : `${removeCount} entfernen & in Master übernehmen`}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <RadioGroup
          value={String(masterId)}
          onValueChange={(v) => onSetMaster(Number(v))}
        >
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-wider text-muted-foreground border-b">
                <tr>
                  <th className="text-left py-1 w-16">Master</th>
                  <th className="text-left py-1">Bezeichnung</th>
                  <th className="text-right py-1 w-20">EP €</th>
                  <th className="text-right py-1 w-24">Samples</th>
                  <th className="text-left py-1 w-24">Status</th>
                  <th className="text-left py-1 w-20">Herkunft</th>
                  <th className="text-left py-1 w-24">Erstellt</th>
                  <th className="text-center py-1 w-16">Entfernen</th>
                </tr>
              </thead>
              <tbody>
                {group.entries.map((entry) => {
                  const p = entry.position;
                  const isMaster = masterId === p.id;
                  const isGlobal = p.userId === null;
                  const isRemoved = selection?.removeIds.has(p.id) ?? false;
                  return (
                    <tr
                      key={p.id}
                      className={`border-b last:border-0 ${
                        isMaster ? "bg-emerald-50/60" : isRemoved ? "bg-red-50/40" : ""
                      }`}
                      data-testid={`dup-entry-${p.id}`}
                    >
                      <td className="py-1.5">
                        <div className="flex items-center gap-1.5">
                          <RadioGroupItem
                            value={String(p.id)}
                            id={`master-${group.id}-${p.id}`}
                            data-testid={`dup-master-${p.id}`}
                          />
                          {isMaster && (
                            <Badge className="h-4 px-1 text-[9px] bg-emerald-600 hover:bg-emerald-600">
                              Master
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="py-1.5">
                        <div className="flex items-start gap-2">
                          <span className="break-words">{p.name}</span>
                          {isGlobal && (
                            <Badge variant="outline" className="h-4 px-1 text-[9px] shrink-0">
                              global
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="py-1.5 text-right font-medium">{p.ep.toFixed(2)}</td>
                      <td className="py-1.5 text-right text-muted-foreground">
                        {entry.sampleCount}
                      </td>
                      <td className="py-1.5">
                        <Badge
                          variant="outline"
                          className={`h-4 px-1 text-[9px] ${
                            p.status === "freigegeben"
                              ? "border-emerald-300 text-emerald-700"
                              : "border-amber-300 text-amber-700"
                          }`}
                        >
                          {p.status === "freigegeben" ? "freigegeben" : "Entwurf"}
                        </Badge>
                      </td>
                      <td className="py-1.5 text-[11px] text-muted-foreground">
                        {QUELLE_LABEL[p.quelle] ?? p.quelle}
                      </td>
                      <td
                        className="py-1.5 text-[11px] text-muted-foreground"
                        data-testid={`dup-created-${p.id}`}
                      >
                        {formatDupDate(p.createdAt)}
                      </td>
                      <td className="py-1.5 text-center">
                        {isMaster ? (
                          <span className="text-[10px] text-muted-foreground italic">–</span>
                        ) : isGlobal ? (
                          <span
                            className="text-[10px] text-muted-foreground italic"
                            title="Globale Standard-Einträge können nicht entfernt werden."
                          >
                            geschützt
                          </span>
                        ) : (
                          <Checkbox
                            checked={isRemoved}
                            onCheckedChange={() => onToggleRemove(p.id)}
                            data-testid={`dup-remove-${p.id}`}
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </RadioGroup>
      </CardContent>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid={`dup-merge-confirm-${group.id}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Duplikate zusammenführen?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  <strong>{removeCount}</strong>{" "}
                  {removeCount === 1 ? "Eintrag" : "Einträge"} werden ausgeblendet
                  (Soft-Delete via <code>aktiv = false</code>) und ihre Preis-Samples
                  werden in den Master übernommen.
                </p>
                <ul className="text-xs text-muted-foreground space-y-1 pl-4 list-disc">
                  <li>
                    Master bleibt: <strong>{masterEntry?.position.name ?? "–"}</strong>{" "}
                    (EP {masterEntry?.position.ep.toFixed(2) ?? "–"} €,{" "}
                    {masterEntry?.sampleCount ?? 0} Samples)
                  </li>
                  <li>
                    Nach dem Merge: ca. <strong>{mergedSampleCount}</strong>{" "}
                    Preis-Samples auf dem Master (Duplikate werden zusammengefasst,
                    max. 50).
                  </li>
                  <li>
                    Bestehende Projekt-Verweise bleiben unverändert – die entfernten
                    Einträge sind nur in der Bibliothek nicht mehr sichtbar.
                  </li>
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmOpen(false);
                onMergeGroup();
              }}
              data-testid={`dup-merge-confirm-action-${group.id}`}
            >
              Zusammenführen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
